import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { CopyLinkIconButton } from '../../components/ui/CopyLinkIconButton';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { OpenIconLink } from '../../components/ui/OpenIconLink';
import { VisibilityIconButton } from '../../components/ui/VisibilityIconButton';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useDocumentationShell } from './documentationShell';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  createDocumentationSpace,
  deleteDocumentationSpace,
  DocumentationSpace,
  listDocumentationSpaces,
  updateDocumentationSpace,
} from '../../services/documentationApi';

export function DocumentationHomePage() {
  const shell = useDocumentationShell();
  const pageSize = shell.kind === 'tenant' ? 10 : 25;
  const { confirm } = useConfirmDialog();
  const canWrite = shell.canWrite;
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();

  const [spaces, setSpaces] = useState<DocumentationSpace[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);

  function resetForm(): void {
    setEditingId(null);
    setName('');
    setDescription('');
    setIsPublic(false);
  }

  function startEdit(space: DocumentationSpace): void {
    expandForm();
    setEditingId(space.id);
    setName(space.name);
    setDescription(space.description ?? '');
    setIsPublic(space.visibility === 'Public');
  }

  async function load(searchTerm = search, targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listDocumentationSpaces({
        page: targetPage,
        pageSize: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const nextItems = result.items ?? [];
      const nextTotal = result.totalCount ?? 0;
      const nextPage = result.page ?? targetPage;
      if (nextItems.length === 0 && nextTotal > 0 && nextPage > 1) {
        setPage(nextPage - 1);
        return;
      }
      setSpaces(nextItems);
      setTotalCount(nextTotal);
      setPage(nextPage);
    } catch {
      toast.error('Falha ao carregar documentos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void load(search, page);
    }, 280);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        const current = spaces.find((space) => space.id === editingId);
        await updateDocumentationSpace(editingId, {
          name: name.trim(),
          description: description.trim() || undefined,
          visibility: isPublic ? 'Public' : 'Private',
          salesProductId: current?.salesProductId,
          sortOrder: current?.sortOrder,
        });
        toast.success('Documento atualizado.');
        collapseForm(resetForm);
      } else {
        await createDocumentationSpace({
          name: name.trim(),
          description: description.trim() || undefined,
          visibility: isPublic ? 'Public' : 'Private',
        });
        toast.success('Documento criado.');
        handleCreateSuccess(resetForm);
      }
      await load();
    } catch {
      toast.error(editingId ? 'Falha ao atualizar documento.' : 'Falha ao criar documento.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleVisibility(space: DocumentationSpace): Promise<void> {
    if (!canWrite) return;
    const next = space.visibility === 'Public' ? 'Private' : 'Public';
    try {
      await updateDocumentationSpace(space.id, {
        name: space.name,
        description: space.description ?? undefined,
        visibility: next,
        salesProductId: space.salesProductId,
        sortOrder: space.sortOrder,
      });
      toast.success(
        next === 'Public'
          ? 'Documento público — link de compartilhamento gerado.'
          : 'Documento tornado privado.',
      );
      if (editingId === space.id) {
        setIsPublic(next === 'Public');
      }
      await load();
    } catch {
      toast.error('Falha ao atualizar visibilidade.');
    }
  }

  async function handleDelete(space: DocumentationSpace): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirm({
        title: 'Excluir documento',
        message: `Excluir “${space.name}” e todas as páginas?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await deleteDocumentationSpace(space.id);
      toast.success('Documento excluído.');
      if (editingId === space.id) {
        collapseForm(resetForm);
      }
      await load();
    } catch {
      toast.error('Falha ao excluir.');
    }
  }

  function copyShareLink(space: DocumentationSpace): void {
    if (!space.shareToken) {
      toast.info('Torne o documento público para gerar o link.');
      return;
    }
    const url = `${window.location.origin}/docs/${space.shareToken}`;
    void navigator.clipboard.writeText(url).then(
      () => toast.success('Link copiado.'),
      () => toast.error('Não foi possível copiar o link.'),
    );
  }

  if (loading && spaces.length === 0 && !search.trim()) {
    return (
      <PageShell>
        <LoadingSpinner label={`Carregando ${shell.homeTitle.toLowerCase()}...`} />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title={shell.homeTitle}
        description={shell.homeDescription}
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar documento' : 'Novo documento'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          createSummary="Clique para cadastrar um novo documento"
          editingSummary={
            editingId
              ? `Editando “${name || 'documento'}” — altere nome, descrição e visibilidade`
              : undefined
          }
          isEditing={Boolean(editingId)}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid md:grid-cols-3">
              <label className="erp-label-block md:col-span-1">
                Nome
                <input
                  className="erp-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={200}
                />
              </label>
              <div className="erp-label-block md:col-span-1">
                Visibilidade
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isPublic}
                    aria-label={isPublic ? 'Documento está público' : 'Documento está privado'}
                    title={isPublic ? 'Documento está público' : 'Documento está privado'}
                    className={`erp-switch ${isPublic ? 'erp-switch-on erp-switch-allow' : ''}`}
                    onClick={() => setIsPublic((current) => !current)}
                  />
                  <span className="text-sm text-secondary-600">
                    {isPublic ? 'Documento está público' : 'Documento está privado'}
                  </span>
                </div>
              </div>
              <label className="erp-label-block md:col-span-2">
                Descrição
                <textarea
                  className="erp-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                  rows={3}
                />
              </label>
            </div>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          className="erp-input-compact h-[42px] w-1/2 min-w-[16rem]"
          placeholder="Buscar documentos por nome ou descrição"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void load(search);
            }
          }}
        />
        <button
          type="button"
          className="inline-flex h-[42px] items-center justify-center rounded-lg bg-primary-500 px-5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          onClick={() => void load(search)}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <LoadingSpinner label="Carregando documentos..." />
      ) : spaces.length === 0 ? (
        <p className="text-secondary-600">
          {search.trim()
            ? 'Nenhum documento encontrado para esta busca.'
            : shell.emptyMessage}
        </p>
      ) : (
        <div className="docs-space-grid">
          {spaces.map((space) => (
            <article key={space.id} className="docs-space-card">
              <div className="docs-space-card__hero">
                <BookOpenIcon className="h-10 w-10 text-white/90" />
              </div>
              <div className="docs-space-card__body">
                <Link to={`${shell.basePath}/${space.id}`} className="docs-space-card__title">
                  {space.name}
                </Link>
                {space.description ? <p className="docs-space-card__desc">{space.description}</p> : null}
                <p className="docs-space-card__meta">
                  {space.pageCount} página(s) · {space.visibility === 'Public' ? 'Público' : 'Privado'}
                </p>
                <div className="docs-space-card__actions">
                  {space.hasPublishedPages && space.shareToken && space.visibility === 'Public' ? (
                    <OpenIconLink to={`/docs/${space.shareToken}`} label="Ver link público" />
                  ) : null}
                  {canWrite ? (
                    <>
                      <EditIconButton
                        label={`Editar nome e descrição de ${space.name}`}
                        onClick={() => startEdit(space)}
                      />
                      <VisibilityIconButton
                        isPublic={space.visibility === 'Public'}
                        label={
                          space.visibility === 'Public'
                            ? 'Tornar documento privado'
                            : 'Tornar documento público'
                        }
                        onClick={() => void handleToggleVisibility(space)}
                      />
                      {space.shareToken ? (
                        <CopyLinkIconButton
                          label="Copiar link de compartilhamento"
                          onClick={() => copyShareLink(space)}
                        />
                      ) : null}
                      <DeleteIconButton
                        label={`Excluir documento ${space.name}`}
                        onClick={() => void handleDelete(space)}
                      />
                    </>
                  ) : null}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
      {!loading && totalCount > pageSize ? (
        <ListPaginationBar
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
        />
      ) : null}
    </PageShell>
  );
}
